// dock-state — which floating dock is open: Notes (bottom) or Documents
// (right edge). Grant, 2026-09-04: "both the notes and the documents
// cannot be opened at the same time. so when notes is open the documents
// floating thing goes away or significantly minimizes and when documents
// is open notes goes away or significantly minimizes."
//
// One tiny shared state, provided once in PageLayout, read by both docks:
// a dock is open only if it is THE open dock, and the other dock's tab
// renders minimized and inert while it is. No reducer, no store — two
// values and a setter.
//
// Falls back to local state when there is no provider (a component test
// rendering one dock alone), so neither dock ever depends on the layout.
//
// CONSUMER: internal-ui.

import { createContext, useContext, useMemo, useState, type ReactNode } from "react";

export type DockName = "notes" | "documents";

interface DockStateValue {
  open: DockName | null;
  setOpen: (dock: DockName | null) => void;
}

const DockStateContext = createContext<DockStateValue | null>(null);

export function DockStateProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState<DockName | null>(null);
  const value = useMemo(() => ({ open, setOpen }), [open]);
  return <DockStateContext.Provider value={value}>{children}</DockStateContext.Provider>;
}

/**
 * The dock's own view of the shared state: `isOpen` for THIS dock,
 * `otherOpen` when the other dock has the floor (render minimized),
 * and `setOpen(bool)` which claims or releases the floor.
 */
export function useDock(name: DockName): {
  isOpen: boolean;
  otherOpen: boolean;
  setOpen: (open: boolean) => void;
} {
  const ctx = useContext(DockStateContext);
  const [local, setLocal] = useState<DockName | null>(null);
  const open = ctx ? ctx.open : local;
  const set = ctx ? ctx.setOpen : setLocal;
  return useMemo(
    () => ({
      isOpen: open === name,
      otherOpen: open !== null && open !== name,
      setOpen: (v: boolean) => set(v ? name : open === name ? null : open),
    }),
    [open, name, set],
  );
}
